/**
 * Song metadata read-out, in DOM.
 *
 * The rendering layer draws the world; text and images belong to the document.
 * That split is the reason the Unity build's most tangled code — a world-space
 * Canvas fighting the raw mouse for the same click — has no equivalent here.
 *
 * For Phase 1 this doubles as the evidence panel: the live position read-out is
 * how you can see that `MusicClock` is advancing against real decoded audio
 * rather than against a frame counter.
 */

export interface SongInfoFields {
  readonly displayName: string;
  readonly author: string;
  readonly bpm: number;
  readonly difficulty: string;
  readonly eventCount: number;
  /** The player`s wave speed. Presentation only; it changes no timing. */
  readonly waveSpeed?: number;
  readonly durationSeconds: number;
  readonly packId: string;
}

const STYLE_ID = 'mandalizm-song-info-style';

const CSS = `
.mandalizm-song-info {
  position: fixed; right: 14px; top: 14px; width: 268px;
  padding: 12px; box-sizing: border-box;
  font: 12px/1.6 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfeff2; background: rgba(0, 8, 14, 0.66);
  border: 1px solid rgba(80, 230, 210, 0.22); border-radius: 8px;
  pointer-events: none; user-select: none;
}
.mandalizm-song-info__cover {
  width: 100%; aspect-ratio: 1 / 1; border-radius: 5px; overflow: hidden;
  margin-bottom: 10px; display: grid; place-items: center;
  background: linear-gradient(140deg, rgba(4,60,64,0.85), rgba(60,20,90,0.85));
  border: 1px solid rgba(90, 220, 210, 0.18);
}
.mandalizm-song-info--demo .mandalizm-song-info__title { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.mandalizm-song-info--demo .mandalizm-song-info__cover,
.mandalizm-song-info--demo .mandalizm-song-info__author,
.mandalizm-song-info--demo .mandalizm-song-info__rows,
.mandalizm-song-info--demo .mandalizm-song-info__note { display:none; }
.mandalizm-song-info__cover img { width: 100%; height: 100%; object-fit: cover; display: block; }
.mandalizm-song-info__cover span {
  font-size: 10px; letter-spacing: 0.14em; color: rgba(160, 220, 225, 0.55);
}
.mandalizm-song-info__title {
  font-size: 15px; line-height: 1.35; color: #eafeff; margin-bottom: 2px;
  text-shadow: 0 0 10px rgba(0, 220, 200, 0.35); word-break: break-word;
}
.mandalizm-song-info__author { color: rgba(150, 210, 225, 0.65); margin-bottom: 8px; }
.mandalizm-song-info__rows { display: grid; grid-template-columns: auto 1fr; gap: 1px 10px; }
.mandalizm-song-info__rows dt { color: rgba(140, 200, 215, 0.6); }
.mandalizm-song-info__rows dd { margin: 0; color: #b8f2ee; text-align: right; }
.mandalizm-song-info__bar {
  height: 3px; margin-top: 9px; border-radius: 2px; overflow: hidden;
  background: rgba(20, 70, 80, 0.55);
}
.mandalizm-song-info__fill {
  height: 100%; width: 0%; background: linear-gradient(90deg, #16e0d0, #8b5cf6);
}
.mandalizm-song-info__note {
  margin-top: 8px; font-size: 11px; color: rgba(140, 210, 220, 0.7);
  border-top: 1px solid rgba(80, 210, 200, 0.16); padding-top: 7px; word-break: break-word;
}
`;

export class SongInfoPanel {
  readonly element: HTMLDivElement;

  private readonly cover: HTMLDivElement;
  private readonly title: HTMLDivElement;
  private readonly author: HTMLDivElement;
  private readonly rows: HTMLDListElement;
  private readonly fill: HTMLDivElement;
  private readonly note: HTMLDivElement;
  private duration = 0;

  /** Whether the diagnostic rows are shown. See `setSong`. */
  private readonly detailed: boolean;

  constructor(parent: HTMLElement = document.body, options: { readonly detailed?: boolean } = {}) {
    this.detailed = options.detailed === true;
    injectStyle();

    this.element = document.createElement('div');
    this.element.className = 'mandalizm-song-info';

    this.cover = document.createElement('div');
    this.cover.className = 'mandalizm-song-info__cover';
    this.cover.innerHTML = '<span>NO COVER</span>';

    this.title = document.createElement('div');
    this.title.className = 'mandalizm-song-info__title';
    this.title.textContent = 'Loading...';

    this.author = document.createElement('div');
    this.author.className = 'mandalizm-song-info__author';

    this.rows = document.createElement('dl');
    this.rows.className = 'mandalizm-song-info__rows';

    const bar = document.createElement('div');
    bar.className = 'mandalizm-song-info__bar';
    this.fill = document.createElement('div');
    this.fill.className = 'mandalizm-song-info__fill';
    bar.appendChild(this.fill);

    this.note = document.createElement('div');
    this.note.className = 'mandalizm-song-info__note';

    this.element.append(this.cover, this.title, this.author, this.rows, bar, this.note);
    parent.appendChild(this.element);
  }

  /**
   * Fills in everything that comes from the manifest and the compiled chart.
   *
   * `displayName` is written with `textContent`, never `innerHTML`: it is
   * author-supplied text and the format explicitly requires it to be shown
   * verbatim, so it must not be able to act as markup.
   */
  /** Shows or hides the whole panel. There is no song to describe on the title. */
  setVisible(visible: boolean): void {
    this.element.hidden = !visible;
  }

  setSong(fields: SongInfoFields): void {
    this.duration = fields.durationSeconds;
    this.title.textContent = fields.displayName;
    this.author.textContent = fields.author.length > 0 ? fields.author : '—';

    this.rows.replaceChildren();
    this.addRow('BPM', fields.bpm.toFixed(fields.bpm % 1 === 0 ? 0 : 1));
    this.addRow('DIFFICULTY', fields.difficulty.toUpperCase());
    this.addRow('EVENTS', fields.eventCount.toLocaleString());
    this.addRow('SPEED', `${Number((fields.waveSpeed ?? 1).toFixed(2))}x`);
    // Live wave count and the pack's storage id are diagnostics, not something
    // a player has any use for. They stay, behind the debug flag, because they
    // are genuinely useful when something looks wrong.
    if (this.detailed) this.addRow('ACTIVE', '0');
    this.addRow('LENGTH', formatTime(fields.durationSeconds));
    if (this.detailed) this.addRow('PACK', fields.packId);
    this.addRow('TIME', '00:00');
  }

  /**
   * Re-labels the chart after a difficulty switch.
   *
   * Both values move together on purpose: a panel showing HARD next to the
   * event count of the chart that was replaced is worse than showing neither,
   * because it looks like it is telling you something.
   */
  setChart(difficulty: string, eventCount: number, waveSpeed?: number): void {
    this.setRow('DIFFICULTY', difficulty.toUpperCase());
    this.setRow('EVENTS', eventCount.toLocaleString());
    if (this.detailed) this.setRow('ACTIVE', '0');
    if (waveSpeed !== undefined) this.setRow('SPEED', `${Number(waveSpeed.toFixed(2))}x`);
  }

  /** Waves currently alive in the simulation. */
  setActiveWaves(count: number): void {
    if (!this.detailed) return;
    this.setRow('ACTIVE', String(count));
  }

  /** Sets the cover art, or restores the placeholder when there is none. */
  setCover(image: ImageBitmap | null): void {
    if (image === null) {
      this.cover.replaceChildren();
      const label = document.createElement('span');
      label.textContent = 'NO COVER';
      this.cover.appendChild(label);
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d')?.drawImage(image, 0, 0);
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.objectFit = 'cover';
    canvas.style.display = 'block';
    this.cover.replaceChildren(canvas);
  }

  /** Live playback position. Driven from `MusicClock`, not from frame time. */
  setPosition(seconds: number): void {
    const clamped = Math.max(0, seconds);
    this.setRow('TIME', formatTime(clamped));
    this.fill.style.width =
      this.duration > 0 ? `${Math.min(100, (clamped / this.duration) * 100).toFixed(2)}%` : '0%';
  }

  setNote(text: string): void {
    this.note.textContent = text;
  }

  dispose(): void {
    this.element.remove();
  }

  private setRow(key: string, value: string): void {
    const cell = this.rows.querySelector(`dd[data-key="${key}"]`);
    if (cell !== null) cell.textContent = value;
  }

  private addRow(key: string, value: string): void {
    const term = document.createElement('dt');
    term.textContent = key;
    const detail = document.createElement('dd');
    detail.dataset['key'] = key;
    detail.textContent = value;
    this.rows.append(term, detail);
  }
}

function formatTime(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
